// The clinic's money screens: Statement of Account (the running ledger) and Pay Company.
//
// The doctor sees the SAME entries the lab sees — that is the point of a ledger. What they don't
// see is anything internal: no cost, no grade, no margin, just what was charged, what was paid, and
// what is left. A payment they submit is a CLAIM shown as "awaiting verification" until the lab
// confirms it; it never quietly moves the balance.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatDate,
  formatMoney,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  rupeesToPaisa,
  WF_LEDGER_ENTRY_LABELS,
  WF_PAYMENT_STATUS_LABELS,
  type PaymentMethod,
  type WfBankAccount,
  type WfInvoice,
  type WfPayment,
  type WfStatement,
} from '@alignova/shared';
import { useLiveEvent } from '../../lib/useLive.ts';
import { portalApi, portalProofUrl, submitPortalPayment } from './portalApi.ts';
import { SkeletonList } from '../../ui/mobile.tsx';

const input = 'w-full rounded-lg border border-line bg-surface px-3 py-2.5 text-[15px] text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25';
const lbl = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted md:mb-0 md:pt-3';
const row = 'block md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4';
const card = 'rounded-xl border border-line bg-surface p-5 shadow-level-1';

function Msg({ ok, children }: { ok?: boolean; children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div className={`rounded-lg px-4 py-3 text-sm ${ok ? 'border border-status-progress-fg/30 bg-status-progress-bg text-status-progress-fg' : 'border border-status-urgent-fg/30 bg-status-urgent-bg text-status-urgent-fg'}`}>{children}</div>
  );
}

function Title({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <header>
      <h1 className="text-xl font-bold text-ink">{children}</h1>
      {sub && <p className="text-sm text-muted">{sub}</p>}
    </header>
  );
}

/** A balance is only "outstanding" when it's positive; a negative one is credit sitting with the lab. */
function BalanceCard({ balance, pending }: { balance: number; pending: number }) {
  const owes = balance > 0;
  return (
    <div className={`${card} flex flex-wrap items-end justify-between gap-4 ${owes ? 'border-status-urgent-fg/30' : ''}`}>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-muted">{owes ? 'Outstanding balance' : balance < 0 ? 'Credit with the lab' : 'Balance'}</div>
        <div className={`font-mono text-3xl font-bold ${owes ? 'text-status-urgent-fg' : 'text-ink'}`}>{formatMoney(Math.abs(balance))}</div>
      </div>
      {pending > 0 && (
        <div className="text-right">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">Awaiting verification</div>
          <div className="font-mono text-lg text-ink">{formatMoney(pending)}</div>
          <div className="text-[11px] text-muted">Not deducted until the lab confirms it</div>
        </div>
      )}
    </div>
  );
}

const statusTone: Record<string, string> = {
  pending: 'bg-status-pending-bg text-status-pending-fg',
  verified: 'bg-status-progress-bg text-status-progress-fg',
  rejected: 'bg-status-urgent-bg text-status-urgent-fg',
};

function PaymentRows({ items }: { items: WfPayment[] }) {
  if (!items.length) return <p className="px-4 py-6 text-center text-sm text-muted">No payments submitted yet.</p>;
  return (
    <ul className="divide-y divide-line">
      {items.map((p) => (
        <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
          <span className="font-mono text-xs text-muted">{p.paymentNumber}</span>
          <span className="font-mono text-sm font-semibold text-ink">{formatMoney(p.amount)}</span>
          <span className="text-sm text-ink">{PAYMENT_METHOD_LABELS[p.method] ?? p.method}</span>
          <span className="text-xs text-muted">{formatDate(p.paidOn)}</span>
          {p.reference && <span className="font-mono text-xs text-muted">{p.reference}</span>}
          {p.hasProof && (
            <a href={portalProofUrl(p.id)} target="_blank" rel="noreferrer" className="text-xs font-medium text-accent hover:underline">
              View proof
            </a>
          )}
          <span className={`ml-auto rounded px-2 py-0.5 text-[11px] font-semibold ${statusTone[p.status] ?? ''}`}>{WF_PAYMENT_STATUS_LABELS[p.status]}</span>
          {p.status === 'rejected' && p.decisionNote && <span className="w-full text-xs text-status-urgent-fg">Reason: {p.decisionNote}</span>}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------------------------
// Statement of Account
// ---------------------------------------------------------------------------------------------

export function PortalLedger() {
  const [range, setRange] = useState({ from: '', to: '' });
  const [s, setS] = useState<WfStatement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    portalApi
      .statement({ from: range.from || undefined, to: range.to || undefined })
      .then(setS)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [range.from, range.to]);
  useEffect(load, [load]);
  useLiveEvent('workflow.billing', load);

  return (
    <div className="space-y-4">
      <Title sub="Every charge and payment on your account, oldest first">Statement of account</Title>
      <Msg>{err}</Msg>
      {s && <BalanceCard balance={s.totals.closingBalance} pending={s.pendingAmount} />}

      <div className="flex flex-wrap items-end gap-3">
        <label>
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">From</span>
          <input type="date" className={input} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
        </label>
        <label>
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">To</span>
          <input type="date" className={input} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
        </label>
        {(range.from || range.to) && (
          <button onClick={() => setRange({ from: '', to: '' })} className="rounded-lg border border-line px-3 py-2.5 text-sm text-ink hover:bg-surface-2">
            Clear
          </button>
        )}
        <button onClick={() => window.print()} className="ml-auto rounded-lg border border-line px-3 py-2.5 text-sm text-ink hover:bg-surface-2">
          Print
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-line bg-surface shadow-level-1">
        <table className="w-full text-sm lg:min-w-[42rem]">
          <thead className="bg-surface-2 text-left text-[11px] uppercase tracking-wider text-muted">
            <tr>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Details</th>
              <th className="px-4 py-2 text-right">Charge</th>
              <th className="px-4 py-2 text-right">Paid</th>
              <th className="px-4 py-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {s && s.openingBalance !== 0 && (
              <tr className="border-t border-line bg-surface-2/50 text-muted">
                <td className="px-4 py-2">{formatDate(s.from ?? '')}</td>
                <td className="px-4 py-2 italic">Balance brought forward</td>
                <td />
                <td />
                <td className="px-4 py-2 text-right font-mono">{formatMoney(s.openingBalance)}</td>
              </tr>
            )}
            {s?.entries.map((e) => (
              <tr key={e.id} className={`border-t border-line ${e.isVoid ? 'text-muted line-through' : ''}`}>
                <td className="px-4 py-2 whitespace-nowrap">{formatDate(e.entryDate)}</td>
                <td className="px-4 py-2">
                  <div className="text-ink">{e.description}</div>
                  <div className="text-[11px] text-muted">
                    {WF_LEDGER_ENTRY_LABELS[e.entryType]}
                    {e.caseNumber ? ` · ${e.caseNumber}` : ''}
                    {e.reference ? ` · ${e.reference}` : ''}
                    {e.isVoid ? ` · Cancelled${e.voidReason ? `: ${e.voidReason}` : ''}` : ''}
                  </div>
                </td>
                <td className="px-4 py-2 text-right font-mono">{e.debit ? formatMoney(e.debit) : ''}</td>
                <td className="px-4 py-2 text-right font-mono">{e.credit ? formatMoney(e.credit) : ''}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{formatMoney(e.balance)}</td>
              </tr>
            ))}
            {s?.entries.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-muted">
                  Nothing on your account for this period.
                </td>
              </tr>
            )}
          </tbody>
          {s && s.entries.length > 0 && (
            <tfoot className="border-t-2 border-line bg-surface-2 font-semibold">
              <tr>
                <td className="px-4 py-2" colSpan={2}>
                  Totals
                </td>
                <td className="px-4 py-2 text-right font-mono">{formatMoney(s.totals.charges)}</td>
                <td className="px-4 py-2 text-right font-mono">{formatMoney(s.totals.payments + s.totals.adjustments)}</td>
                <td className="px-4 py-2 text-right font-mono">{formatMoney(s.totals.closingBalance)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Pay Company
// ---------------------------------------------------------------------------------------------

export function PortalPay() {
  const [accounts, setAccounts] = useState<WfBankAccount[]>([]);
  const [payments, setPayments] = useState<WfPayment[]>([]);
  const [balance, setBalance] = useState<{ owed: number; pending: number } | null>(null);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [f, setF] = useState({ amount: '', method: 'bank_transfer' as PaymentMethod, bankAccountId: '', reference: '', paidOn: today, note: '' });
  const [proof, setProof] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const load = useCallback(() => {
    void portalApi.bankAccounts().then(setAccounts).catch(() => setAccounts([]));
    void portalApi.payments().then(setPayments).catch(() => setPayments([]));
    void portalApi
      .statement()
      .then((s) => setBalance({ owed: s.totals.closingBalance, pending: s.pendingAmount }))
      .catch(() => setBalance(null));
  }, []);
  useEffect(load, [load]);
  useLiveEvent('workflow.billing', load);

  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
  const paisa = rupeesToPaisa(f.amount);

  const submit = async () => {
    if (!paisa || paisa <= 0) {
      setErr('Enter the amount you paid.');
      return;
    }
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      const p = await submitPortalPayment(
        {
          amount: paisa,
          method: f.method,
          bankAccountId: f.bankAccountId || null,
          reference: f.reference || null,
          paidOn: f.paidOn,
          note: f.note || null,
        },
        proof,
      );
      setOk(`Payment ${p.paymentNumber} submitted - the lab will verify it and it will then show on your statement.`);
      setF({ amount: '', method: 'bank_transfer', bankAccountId: '', reference: '', paidOn: today, note: '' });
      setProof(null);
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Title sub="Pay into one of the lab's accounts, then tell us here so it can be credited">Pay company</Title>
      <Msg>{err}</Msg>
      <Msg ok>{ok}</Msg>
      {balance && <BalanceCard balance={balance.owed} pending={balance.pending} />}

      {accounts.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">Where to pay</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {accounts.map((a) => (
              <div key={a.id} className={card}>
                <div className="font-semibold text-ink">{a.label}</div>
                <dl className="mt-2 space-y-1 text-sm">
                  <div className="flex gap-2">
                    <dt className="w-24 shrink-0 text-muted">Bank</dt>
                    <dd className="text-ink">{a.bankName}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-24 shrink-0 text-muted">Title</dt>
                    <dd className="text-ink">{a.accountTitle}</dd>
                  </div>
                  {a.accountNumber && (
                    <div className="flex gap-2">
                      <dt className="w-24 shrink-0 text-muted">Account</dt>
                      <dd className="font-mono text-ink">{a.accountNumber}</dd>
                    </div>
                  )}
                  {a.iban && (
                    <div className="flex gap-2">
                      <dt className="w-24 shrink-0 text-muted">IBAN</dt>
                      <dd className="font-mono text-ink">{a.iban}</dd>
                    </div>
                  )}
                  {a.branch && (
                    <div className="flex gap-2">
                      <dt className="w-24 shrink-0 text-muted">Branch</dt>
                      <dd className="text-ink">{a.branch}</dd>
                    </div>
                  )}
                </dl>
                {a.instructions && <p className="mt-2 whitespace-pre-wrap text-xs text-muted">{a.instructions}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">Tell us about a payment</h2>
        <div className={`${card} space-y-3`}>
          <label className={row}>
            <span className={lbl}>Amount (PKR) *</span>
            <input className={input} inputMode="decimal" autoFocus value={f.amount} onChange={(e) => set('amount', e.target.value)} placeholder="e.g. 25000" />
          </label>
          <label className={row}>
            <span className={lbl}>Method *</span>
            <select className={input} value={f.method} onChange={(e) => set('method', e.target.value)}>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          </label>
          {accounts.length > 0 && (
            <label className={row}>
              <span className={lbl}>Paid into</span>
              <select className={input} value={f.bankAccountId} onChange={(e) => set('bankAccountId', e.target.value)}>
                <option value="">Not applicable</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label} - {a.bankName}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className={row}>
            <span className={lbl}>Date paid *</span>
            <input type="date" className={input} value={f.paidOn} max={today} onChange={(e) => set('paidOn', e.target.value)} />
          </label>
          <label className={row}>
            <span className={lbl}>Reference</span>
            <input className={input} value={f.reference} onChange={(e) => set('reference', e.target.value)} placeholder="Transaction ID / cheque no." />
          </label>
          <label className={row}>
            <span className={lbl}>Proof</span>
            <span className="block">
              <input type="file" accept="image/*,.pdf" className="text-sm" onChange={(e) => setProof(e.target.files?.[0] ?? null)} />
              <span className="mt-1 block text-xs text-muted">A screenshot or slip speeds verification up. Images and PDF only.</span>
            </span>
          </label>
          <label className={row}>
            <span className={lbl}>Note</span>
            <textarea className={input} rows={2} value={f.note} onChange={(e) => set('note', e.target.value)} placeholder="Anything we should know - e.g. which cases this covers" />
          </label>
          {paisa !== null && paisa > 0 && <p className="text-sm text-muted md:pl-[11.75rem]">You are declaring {formatMoney(paisa)}.</p>}
          <div className="flex justify-end">
            <button
              onClick={() => void submit()}
              disabled={busy || !f.amount}
              className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-fg disabled:opacity-50"
            >
              {busy ? 'Submitting…' : 'Submit payment'}
            </button>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">Your payments</h2>
        <div className="rounded-xl border border-line bg-surface shadow-level-1">
          <PaymentRows items={payments} />
        </div>
        {payments.some((p) => p.status === 'verified') && (
          <p className="text-xs text-muted">
            Verified payments appear on your{' '}
            <a href="/portal/ledger" className="text-accent hover:underline">
              statement of account
            </a>
            .
          </p>
        )}
      </section>
    </div>
  );
}

/** Exported for the dashboard tile — keeps the money vocabulary in one file. */
export function OutstandingTile({ balance, pending, onClick }: { balance: number; pending: number; onClick: () => void }) {
  const owes = balance > 0;
  return (
    <button onClick={onClick} className={`${card} w-full text-left transition hover:border-accent/50 ${owes ? 'border-status-urgent-fg/30' : ''}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">{owes ? 'Outstanding balance' : balance < 0 ? 'Credit with the lab' : 'Account balance'}</div>
      <div className={`font-mono text-2xl font-bold ${owes ? 'text-status-urgent-fg' : 'text-ink'}`}>{formatMoney(Math.abs(balance))}</div>
      <div className="text-[11px] text-muted">{pending > 0 ? `${formatMoney(pending)} awaiting verification` : 'View statement'}</div>
    </button>
  );
}

// ---------------------------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------------------------

/**
 * The clinic's invoices.
 *
 * One is raised as soon as the lab sets a case's aligner count — the count is what prices the case
 * — so a doctor sees what a case costs without asking. Payments settle the ACCOUNT rather than a
 * specific invoice, which is how this lab works, so there is no "paid" flag per row: what is
 * outstanding is on the statement.
 */
export function PortalInvoices() {
  const [items, setItems] = useState<WfInvoice[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<WfInvoice | null>(null);

  const load = useCallback(() => {
    portalApi
      .invoices()
      .then(setItems)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(load, [load]);
  // An invoice the lab edits (re-issued, original voided) shows here without a reload.
  useLiveEvent('workflow.billing', load);

  const live = items?.filter((i) => i.status === 'issued') ?? [];
  const total = live.reduce((a, i) => a + i.total, 0);

  if (err) return <div className="rounded-lg border border-status-urgent-fg/30 bg-status-urgent-bg px-4 py-3 text-sm text-status-urgent-fg">{err}</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[1.6rem] font-bold leading-tight tracking-tight text-ink lg:text-xl">Invoices</h1>
          <p className="mt-0.5 text-sm text-muted">Every case the lab has priced for you.</p>
        </div>
        <span className="tabnum text-sm text-muted">
          {live.length} invoice{live.length === 1 ? '' : 's'} · {formatMoney(total)}
        </span>
      </div>

      {!items && <SkeletonList rows={4} />}
      {items && !items.length && (
        <div className="rounded-lg border border-dashed border-line px-6 py-10 text-center text-sm text-muted">
          No invoices yet. One appears here once the lab sets the number of aligners for a case.
        </div>
      )}

      {items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((i) => (
            <li key={i.id}>
              <button
                type="button"
                onClick={() => setOpen(i)}
                className="tap-row flex w-full items-center gap-3 rounded-lg border border-line bg-surface p-4 text-left shadow-level-1"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-semibold text-ink">{i.invoiceNumber}</span>
                    {i.status === 'void' && (
                      <span className="rounded bg-status-urgent-bg px-1.5 py-0.5 text-[11px] font-bold uppercase text-status-urgent-fg">
                        Void
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block truncate text-sm text-muted">
                    {i.patientName ?? '-'}
                    {i.caseNumber ? ` · ${i.caseNumber}` : ''} · {formatDate(i.issueDate)}
                  </span>
                </span>
                <span className={`tabnum shrink-0 font-semibold ${i.status === 'void' ? 'text-muted line-through' : 'text-ink'}`}>
                  {formatMoney(i.total)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div
          className="fixed inset-0 z-modal flex items-end justify-center bg-navy/50 p-0 animate-fade sm:items-center sm:p-4"
          onMouseDown={(e) => e.target === e.currentTarget && setOpen(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Invoice ${open.invoiceNumber}`}
        >
          <div className="animate-sheet max-h-[88dvh] w-full overflow-y-auto rounded-sheet bg-surface p-5 shadow-level-3 sm:max-w-lg sm:rounded-lg">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-lg font-bold text-ink">{open.invoiceNumber}</div>
                <div className="text-xs text-muted">{formatDate(open.issueDate)}</div>
              </div>
              <span className="flex items-center gap-3">
                <Link
                  to={`/portal/invoices/${open.id}/print`}
                  className="tap text-sm font-semibold text-accent"
                >
                  Print / PDF
                </Link>
                <button type="button" onClick={() => setOpen(null)} className="tap text-sm font-medium text-muted">
                  Close
                </button>
              </span>
            </div>

            <div className="mb-3 text-sm text-muted">
              {open.patientName ?? '-'}
              {open.caseNumber ? ` · case ${open.caseNumber}` : ''}
            </div>

            <table className="w-full text-sm">
              <tbody>
                {open.lines.map((l) => (
                  <tr key={l.id} className="border-b border-line">
                    <td className="py-2">
                      {l.description}
                      <div className="tabnum text-[11px] text-muted">
                        {l.quantity} x {formatMoney(l.unitAmount)}
                      </div>
                    </td>
                    <td className="tabnum py-2 text-right font-medium">{formatMoney(l.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="py-2 font-semibold">Total</td>
                  <td className="tabnum py-2 text-right text-base font-bold text-ink">{formatMoney(open.total)}</td>
                </tr>
              </tbody>
            </table>

            {open.status === 'void' && (
              <p className="mt-3 rounded-md bg-status-urgent-bg px-3 py-2 text-sm text-status-urgent-fg">
                This invoice was voided{open.voidReason ? ` - ${open.voidReason}` : ''}. It is not owed.
              </p>
            )}
            <p className="mt-3 text-xs text-muted">
              Payments are credited to your account rather than to a single invoice - your statement
              shows what is outstanding overall.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
